// @archigraph oPgyVz0Y
// @archigraph AUAUzsIW
// Landing page for archigraph.io

import { Link } from 'react-router-dom';
import { Container } from '../components/ui/Container';

const examples = [
  {
    prompt: 'Build a production-ready ticketing SaaS',
    nodes: [
      'API Gateway', 'Auth Service', 'Ticket Service', 'Notification Service',
      'Billing Service', 'Users DB', 'Tickets DB', 'Redis Cache',
      'Email Queue', 'Webhook Workers', 'React Dashboard', 'Admin Panel',
    ],
  },
  {
    prompt: 'Create a real-time multiplayer game backend',
    nodes: [
      'WebSocket Gateway', 'Matchmaking Service', 'Game State Service',
      'Leaderboard Service', 'Auth Service', 'Player DB', 'Redis Pub/Sub',
      'Game Sessions Cache', 'Replay Storage', 'Analytics Pipeline',
    ],
  },
  {
    prompt: 'Set up an e-commerce platform with inventory management',
    nodes: [
      'Storefront SPA', 'Product Catalog API', 'Cart Service', 'Checkout Service',
      'Payment Gateway', 'Inventory Service', 'Order Service', 'Fulfillment Queue',
      'Product DB', 'Orders DB', 'Search Index', 'CDN',
    ],
  },
  {
    prompt: 'Build a CI/CD platform like GitHub Actions',
    nodes: [
      'API Server', 'Runner Orchestrator', 'Container Scheduler', 'Artifact Storage',
      'Log Aggregator', 'Webhook Ingestion', 'Pipeline DB', 'Queue',
      'Secret Manager', 'Dashboard UI', 'Notification Service',
    ],
  },
];

function PromptExample({ prompt, nodes }: { prompt: string; nodes: string[] }) {
  return (
    <div className="border border-border">
      <div className="p-6 border-b border-border">
        <p className="text-xs text-text-lighter mb-2">You say:</p>
        <p className="text-sm text-text font-bold">&ldquo;{prompt}&rdquo;</p>
      </div>
      <div className="p-6">
        <p className="text-xs text-text-lighter mb-3">ArchiGraph generates:</p>
        <div className="flex flex-wrap gap-1.5">
          {nodes.map((node) => (
            <span
              key={node}
              className="text-[10px] px-2 py-1 bg-primary/10 text-primary border border-primary/20"
            >
              {node}
            </span>
          ))}
        </div>
        <p className="text-[10px] text-text-lighter mt-3">
          + {Math.floor(nodes.length * 1.8)} edges defining every protocol, contract, and data flow
        </p>
      </div>
    </div>
  );
}

export function LandingPage() {
  return (
    <>
      {/* Hero */}
      <section className="py-16 md:py-32">
        <Container>
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-text mb-4 leading-[1.1] tracking-tight md:whitespace-nowrap">
              One prompt. <span className="text-primary">Entire system.</span>
            </h1>
            <p className="text-lg md:text-2xl text-text-light mb-8 max-w-xl leading-snug">
              A graph of connected prompts describing large systems.
            </p>
            <p className="text-sm text-text-lighter mb-10 max-w-xl leading-relaxed">
              Describe what you want to build. ArchiGraph decomposes it into
              services, databases, APIs, and queues — then AI agents build each
              piece with full knowledge of the whole.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/download"
                className="inline-flex items-center justify-center gap-2 rounded-none font-bold uppercase tracking-wider transition-all duration-150 bg-primary text-bg hover:bg-primary-light px-8 py-3 text-sm min-h-[44px]"
              >
                Download
              </Link>
              <Link
                to="/docs"
                className="inline-flex items-center justify-center gap-2 rounded-none font-medium uppercase tracking-wider transition-all duration-150 bg-transparent text-text-light hover:text-text border border-border hover:border-text-lighter px-8 py-3 text-sm min-h-[44px]"
              >
                Learn more
              </Link>
            </div>
          </div>
          <p className="text-sm text-text-lighter mt-12 mb-3">&ldquo;Make an enterprise ready chat app like Slack&rdquo;</p>
          <div className="border border-border">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full"
            >
              <source src="/archigraph-demo.mp4" type="video/mp4" />
            </video>
          </div>
        </Container>
      </section>

      {/* How it works — 3 steps, tight */}
      <section className="pb-24">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-px md:bg-border">
            <div className="bg-bg p-6 md:p-8 border border-border md:border-0">
              <span className="text-primary font-bold text-sm">01</span>
              <h3 className="text-sm font-bold text-text uppercase tracking-wider mt-3 mb-2">Describe</h3>
              <p className="text-xs text-text-lighter leading-relaxed">
                Tell ArchiGraph what you want to build in plain language. It
                decomposes your idea into a complete architecture graph.
              </p>
            </div>
            <div className="bg-bg p-6 md:p-8 border border-border md:border-0">
              <span className="text-primary font-bold text-sm">02</span>
              <h3 className="text-sm font-bold text-text uppercase tracking-wider mt-3 mb-2">Generate</h3>
              <p className="text-xs text-text-lighter leading-relaxed">
                Every node gets an implementation brief. Every edge carries the
                protocol, auth, and contract details agents need.
              </p>
            </div>
            <div className="bg-bg p-6 md:p-8 border border-border md:border-0">
              <span className="text-primary font-bold text-sm">03</span>
              <h3 className="text-sm font-bold text-text uppercase tracking-wider mt-3 mb-2">Delegate</h3>
              <p className="text-xs text-text-lighter leading-relaxed">
                Hand each component to an AI coder. Every agent sees the full
                system — not just its piece — so services integrate on the first try.
              </p>
            </div>
          </div>
          <div className="border-l-2 border-primary pl-6 mt-8">
            <p className="text-sm text-text leading-relaxed">
              <span className="text-primary font-bold">Every agent gets the full picture.</span>{' '}
              Each component's brief includes its neighbors, their contracts, and how
              they connect — so 10 agents can build 10 services in parallel and they all fit together.
            </p>
          </div>
        </Container>
      </section>

      {/* Prompt examples */}
      <section className="pb-24">
        <Container>
          <p className="text-xs uppercase tracking-widest text-primary mb-3 font-bold">
            // one prompt → full architecture
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-text mb-10 max-w-2xl">
            You describe the system. ArchiGraph architects it.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-px md:bg-border">
            {examples.map((ex) => (
              <PromptExample key={ex.prompt} {...ex} />
            ))}
          </div>
        </Container>
      </section>

      {/* Example app built with ArchiGraph */}
      <section className="pb-24">
        <Container>
          <p className="text-xs uppercase tracking-widest text-primary mb-3 font-bold">
            // built with archigraph
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-text mb-4 max-w-2xl">
            See it in action.
          </h2>
          <p className="text-sm text-text-light mb-8 max-w-xl leading-relaxed">
            SketchCraft is a free, open-source 3D modeling app built entirely from an ArchiGraph.
            Electron + React + Three.js — architected as a graph, implemented by AI agents.
          </p>
          <div className="border border-border p-6 md:p-8 max-w-xl">
            <h3 className="text-sm font-bold text-text mb-1">SketchCraft</h3>
            <p className="text-xs text-text-lighter mb-5">
              3D modeling desktop app &middot; Electron &middot; React &middot; Three.js &middot; TypeScript
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/viewer?archigraph=https://raw.githubusercontent.com/CacheFactory/SketchCraft/main/archigraph.yaml&schema=https://raw.githubusercontent.com/CacheFactory/SketchCraft/main/schema.yaml"
                className="inline-flex items-center justify-center gap-2 rounded-none font-bold uppercase tracking-wider transition-all duration-150 bg-primary text-bg hover:bg-primary-light px-6 py-2.5 text-xs min-h-[44px]"
              >
                View Architecture
              </a>
              <a
                href="https://github.com/CacheFactory/SketchCraft"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-none font-medium uppercase tracking-wider transition-all duration-150 bg-transparent text-text-light hover:text-text border border-border hover:border-text-lighter px-6 py-2.5 text-xs min-h-[44px]"
              >
                GitHub Repo &rarr;
              </a>
            </div>
          </div>
        </Container>
      </section>

      {/* Why this matters — short and punchy */}
      <section className="pb-24">
        <Container>
          <p className="text-xs uppercase tracking-widest text-primary mb-3 font-bold">
            // the problem with AI today
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-text mb-4 max-w-2xl">
            AI builds components. Not systems.
          </h2>
          <p className="text-sm text-text-light mb-10 max-w-xl leading-relaxed">
            Without shared context, each AI session makes different assumptions
            about protocols, auth, schemas, and contracts. ArchiGraph fixes this
            — one architecture graph that every agent reads before writing a line.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-px md:bg-border max-w-3xl">
            <div className="bg-bg p-6 md:p-8 border border-border md:border-0">
              <h3 className="text-xs font-bold text-error uppercase tracking-wider mb-4">
                Without ArchiGraph
              </h3>
              <ul className="space-y-2.5">
                {[
                  'Service A uses REST, Service B uses gRPC',
                  'Each agent invents its own auth scheme',
                  'Integration breaks found after hours of coding',
                  'Architecture lives in someone\'s head',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-text-lighter">
                    <span className="text-error mt-0.5 shrink-0">&times;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-bg p-6 md:p-8 border border-border md:border-0">
              <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-4">
                With ArchiGraph
              </h3>
              <ul className="space-y-2.5">
                {[
                  'Every service reads the same architecture file',
                  'Protocols, ports, auth — defined once',
                  'Components integrate on first build',
                  'Architecture stays current as you implement',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-text-lighter">
                    <span className="text-primary mt-0.5 shrink-0">+</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

    </>
  );
}
